import type { ReactNode } from 'react';
import { Text } from '@clay-run/glaze/text';
import type { EnrollmentItem, InputFieldOptions, SenderOption } from '@shared/flow/enrollment';
import type { ActivePlayType, PlayOption } from '@shared/flow/play-catalog';
import type { GongFlowSummary } from '@shared/flow/gong-flow-catalog';
import type { ItemAutosaveOwnership, ItemAutosavePatch } from '../../EnrollQueue/enroll-queue-types';
import { findConnectorField } from '../../EnrollQueue/connector-field';
import { EnrollmentItemEditor, type CopyPrefill } from '../../EnrollQueue/EnrollmentItemEditor';
import { RECENT_SEND_BLOCK_LABEL } from '../../EnrollQueue/itemBlockReason';
import { overriddenAccessory } from './overriddenAccessory';
import type { StepOverrideSnapshot } from '../../EnrollQueue/StepOverrideEditor';
import type { StagedStepCopy, IdentityKey } from '../../EnrollQueue/state/staged-play-config';
import { useIsSenderTouched } from '../../EnrollQueue/state/staged-play-config';
import { isBulkFollowEligible } from '../../EnrollQueue/bulk-step-defaults';
import type { DockSendHandlers } from '../../EnrollQueue/useDockSendHandlers';
import { useUserControlsContext } from '../../../../contexts/UserControlsContext';
import { StepRows } from './StepRows';

/**
 * The editor's step callbacks are optional on `LeadEditorContext`, but
 * `EnrollmentItemEditor` only CALLS `stepsSection` once every one of them is
 * present — so this is unreachable in practice and exists only to keep
 * `subscribeStep`'s return type a function rather than `undefined`.
 */
const NOOP_UNSUBSCRIBE = () => {};

/**
 * The data/callbacks a `LeadRow`/`LeadExpandedBody` pair needs to mount
 * `EnrollmentItemEditor`, (when overridden) `overriddenAccessory`, the
 * identity/will-skip lines (U4b), and — since rail-glaze-parity Change 2 —
 * `LeadRow`'s own "Switch"/"Clear" trailing buttons and inline reassign
 * `PlayPicker` — the SAME prop set `AssignedRow.tsx` threads today (see its
 * own mount at `:864`), minus V1-only row chrome (`onUnstick`/`onCancel`/
 * `flashRequest`) this unit doesn't own. `LeadRow` extends this with its own
 * row-only field (`expanded`, plus the PM1/terra-ohe.8 stuck/queued recovery
 * props); `LeadExpandedBody` takes exactly this shape (though it no longer
 * reads every field in it — see this file's own doc comment below), so the
 * two never drift out of sync on what the editor/row needs.
 */
export interface LeadEditorContext {
  item: EnrollmentItem;
  readOnly: boolean;
  /** Send gate, distinct from `readOnly` (edit lock). Defaults to `readOnly`. */
  sendReadOnly?: boolean;
  /** `EnrollQueueContext.isItemBusy(item.id)`, resolved once by the caller (same posture as `AssignedRow`'s own `isItemBusy` consumers). */
  isItemBusy: (itemId: number) => boolean;
  /**
   * The one `useDockSendHandlers()` object for this dock (D39) — `LeadRow`'s
   * own Send/Send anyway buttons and `LeadExpandedBody`'s `onSetStatus`/
   * `onSend`/`onToggleCoolOff` all resolve through it, never a second
   * `useSendGroupPipeline` instance.
   */
  handlers: DockSendHandlers;
  /**
   * `EnrollQueueContext.baseCopyDraftStore.identity` — feeds `useIsSenderTouched`
   * (the Overridden badge's sender half, D16) and `overriddenAccessory`'s
   * reset. `null` degrades both to "never touched", matching every other
   * consumer of this identity (`PlayQueueCard`, `ContactQueueRow`).
   */
  identity: IdentityKey | null;
  /**
   * The play's effective sender a "Reset to base copy" re-materializes onto
   * the row (`PlayQueueCard`'s `resetBaseSenderFor` derivation) — computed by
   * the caller, which alone knows the play/staged-sender rung. Never
   * re-derived here (D30).
   */
  baseSender: string | null;
  /** `EnrollQueueContext.isSending || handlers.groupSendPending`, OR'ed by the caller exactly as `RailDock` already ORs it for `QueueBody` — see `AssignedRow`'s own `sending` prop doc. */
  sending: boolean;
  senders: SenderOption[];
  inputOptions?: InputFieldOptions[];
  onSelectInputOption?: (itemId: number, fieldKey: string, persistsTo: InputFieldOptions['persistsTo'], value: string | null) => Promise<void>;
  onRefreshInputOptionsIfStale?: (itemId: number) => void;
  copyPrefill?: CopyPrefill;
  senderPrefill?: string | null;
  onSenderTouched?: (itemId: number) => void;
  hasEditableCopy?: boolean;
  copyRequired?: boolean;
  onAutosave: (itemId: number, patch: ItemAutosavePatch, owned: ItemAutosaveOwnership) => void;
  onFlushSave: (itemId: number) => Promise<void>;
  gongFlowId: string | null;
  bodyFormat?: 'plain' | 'rich';
  onEditStepOverride?: (itemId: number, stepNumber: number, stepType: string, values: StepOverrideSnapshot) => void;
  onResetStepOverride?: (itemId: number, stepNumber: number, stepType: string) => void;
  stepOverrideDraftFor?: (itemId: number, stepNumber: number) => { values: StepOverrideSnapshot | null } | null;
  stepSaveErrorFor?: (itemId: number, stepNumber: number) => string | null;
  subscribeStepOverrides?: (itemId: number, stepNumber: number, onChange: () => void) => () => void;
  onSaveStepOverrides: (itemId: number) => Promise<void>;
  batchStepCopy?: Readonly<Record<string, StagedStepCopy>>;
  onBeginSendPipeline: (itemIds: number[]) => boolean;
  onEndSendPipeline: (itemIds: number[]) => void;

  // ── U4b additions (identity line, will-skip line, Switch/Clear + reassign picker) ──────────────
  /** By Contact only (D16) — appended to the identity line; `undefined` in By Play (the section header already names the play), dropped by the identity line's own `filter(Boolean)`. */
  playName?: string;
  /**
   * This row's one block label — SAME value `LeadRow`'s pill derives from
   * (`blockPillFor`), never re-derived here (D30). Drives the "Will skip —
   * {label}" line: shown whenever non-null UNLESS it's exactly the
   * recent-send label, which means the item's only issue IS "merely
   * recent-send-flagged" (E19/E20 — `itemBlockReason`'s priority order
   * means any OTHER block, e.g. `Needs a sender`, always wins the label
   * even when the item is also recent-send-flagged).
   */
  blockLabel: string | null;
  /**
   * D19's lifted row-expansion toggle. `LeadRow`'s reassign sequence
   * (`useReassignPlay`'s `setEditorOpen(false)`) collapses this row through
   * it — only if currently expanded — the instant a play change is picked —
   * the V1 stale-draft-unmount fix (`AssignedRow`'s matching comment), ported
   * to V2's lifted expansion model: there is no separate "editor open" state,
   * so "close the editor" IS "collapse the row" (a no-op when the row was
   * never expanded in the first place — rail-glaze-parity Change 2, a
   * "Switch" click on a collapsed row must not accidentally expand it).
   */
  onToggleExpanded: (itemId: number) => void;
  /** `EnrollQueueContext.plays` — threaded to `LeadRow`'s own inline "Switch" picker (D41, moved from the expanded body in rail-glaze-parity Change 2). */
  plays: PlayOption[];
  /** `EnrollQueueContextValue.activePlayTypes` — ditto. */
  activePlayTypes: ActivePlayType[];
  /** `EnrollQueueContext.gongFlowsSuggested` — ditto; omitted degrades to `PlayPicker`'s own empty default. */
  gongFlowsSuggested?: GongFlowSummary[];
  /**
   * `EnrollQueueContext.assignPlay` — D41's `onReassign`, the SAME mutation
   * `AssignedRow`'s "Change Play" uses (framed as a move, the item is
   * already assigned). Optional only so a caller that hasn't wired it yet
   * doesn't fail to compile (same posture as `onUnstick`/`onCancel`
   * elsewhere on this row); every real mount (`PlaysPane`) passes it.
   */
  onReassign?: (itemId: number, play: PlayOption) => void | Promise<void>;
  /** `EnrollQueueContext.clearStepOverridesForItem` — D41's `onClearStepOverrides`. */
  onClearStepOverrides?: (itemId: number) => () => void;
  /** `EnrollQueueContext.requestQueueFlash` — D41's post-reassign flash cue; optional, same degrade as `AssignedRow`'s own prop. */
  requestQueueFlash?: (contactId: string, opts?: { autoOpenPicker?: boolean; openEditorAfterAssign?: boolean }) => void;
  /** `EnrollQueueContext.removeItem` — `LeadRow`'s "Clear" action (renamed from "Remove" in rail-glaze-parity Change 2). Optional for the same back-compat reason as `onReassign`. */
  onRemove?: (itemId: number) => void;
}

export type LeadExpandedBodyProps = LeadEditorContext;

/**
 * An open lead's editor (plan 2026-09-30-002, U2 Approach — "U2 scope only").
 * Embeds `EnrollmentItemEditor` with the SAME props `AssignedRow` passes
 * today, plus `hideSendButton` (D25: `LeadRow`'s own trailing Send/Send
 * anyway button is the row's ONE send affordance — the editor's built-in
 * "Send 1" would be a second, redundant one in this shell) and `stepsSection`
 * (D25/U3: the lead rung of the Glaze step editor, `StepRows scope="lead"`).
 *
 * `stepsSection` replaces the editor's whole `GongFlowStepsSection`
 * (`EnrollmentItemEditor.tsx:1328`) with `StepRows scope="lead"`. The two
 * things `GongFlowStepsSection` also rendered beneath its step editor — the
 * `Override cool-off` checkbox and the `Enrolls as you` owner note — are
 * rendered by `StepRows`' own lead-only `coolOff` prop (U4b fix, plan
 * 2026-09-30-002-fix), reading `item.cool_off_override` and this component's
 * own `useUserControlsContext()` email directly (neither crosses
 * `EnrollmentItemEditor`'s `stepsSection` callback args), with V1's exact
 * copy — so the flag flips no Gong-enrolment behaviour.
 *
 * Above `EnrollmentItemEditor`: the identity line (title · email · play
 * name) and, when this row will be skipped at send time for a reason other
 * than "merely recent-send-flagged", the `Will skip — <reason>` line
 * (E19/E20). Below it: `overriddenAccessory` (D26, relocated here from U2b's
 * interim under-the-editor placement — same render, same gating).
 *
 * rail-glaze-parity (2026-10-01, Change 2) — `Change Play`/`Remove` (D41's
 * reassign sequence and its inline `PlayPicker`) moved OUT of this footer and
 * into `LeadRow`'s collapsed-row `trailing` slot as "Switch"/"Clear", so a
 * rep can act without expanding the row at all — `LeadRow` now owns
 * `reassignOpen`, `useReassignPlay`, and the inline `PlayPicker` mount
 * itself, matching `UnassignedRow`'s own precedent (trigger button and
 * picker mount co-located in ONE component, not split across a row/body
 * pair). This body no longer reads `onReassign`/`onClearStepOverrides`/
 * `requestQueueFlash`/`onRemove`/`plays`/`activePlayTypes`/
 * `gongFlowsSuggested`/`onToggleExpanded` — they stay on `LeadEditorContext`
 * (this file's prop type) only because `LeadRow` needs them too and the two
 * share one interface (D30).
 *
 * Never lifts `EnrollmentItemEditor`'s own draft (`useDraftState` inside that
 * component already survives an SSE re-render) — see the Draft State table.
 */
export function LeadExpandedBody({
  item,
  readOnly,
  sendReadOnly,
  isItemBusy,
  handlers,
  identity,
  baseSender,
  sending,
  senders,
  inputOptions,
  onSelectInputOption,
  onRefreshInputOptionsIfStale,
  copyPrefill,
  senderPrefill,
  onSenderTouched,
  hasEditableCopy,
  copyRequired,
  onAutosave,
  onFlushSave,
  gongFlowId,
  bodyFormat,
  onEditStepOverride,
  onResetStepOverride,
  stepOverrideDraftFor,
  stepSaveErrorFor,
  subscribeStepOverrides,
  onSaveStepOverrides,
  batchStepCopy,
  onBeginSendPipeline,
  onEndSendPipeline,
  playName,
  blockLabel,
}: LeadExpandedBodyProps): ReactNode {
  const itemBusy = isItemBusy(item.id);
  // U4b fix — the cool-off owner note's "you" needs the acting rep's own
  // email, same read `EnrollmentItemEditor` itself performs for V1's
  // `GongFlowStepsSection` (its own `useUserControlsContext()` call);
  // threaded straight into `StepRows`' `coolOff` prop below rather than
  // through `stepsSection`'s callback args, which carry neither this nor
  // `item.cool_off_override`.
  const { email: actingRepEmail } = useUserControlsContext();
  const senderTouched = useIsSenderTouched(identity, item.id);
  // U5 — `findConnectorField` is the single shared predicate
  // (`connector-field.ts`), also used by `LeadRow`'s own `isRequest` and
  // `EnrollmentItemEditor.tsx`'s `connectorField` — never re-derived
  // differently between the row and its body (D30).
  const isRequest = !!findConnectorField(inputOptions);

  return (
    <div className="flex flex-col gap-3">
      <Text size="xs" variant="tertiary">
        {[item.contact_title, item.contact_email ?? 'No email', playName].filter(Boolean).join(' · ')}
      </Text>
      {isRequest && <Text size="xs" variant="tertiary">Sends a multithread request</Text>}
      {blockLabel !== null && blockLabel !== RECENT_SEND_BLOCK_LABEL && (
        <Text size="xs" variant="warning">Will skip — {blockLabel}</Text>
      )}
      <EnrollmentItemEditor
        item={item}
        readOnly={readOnly}
        sendReadOnly={sendReadOnly ?? readOnly}
        senders={senders}
        // U7 (rail-glaze-parity) — `LeadRow`'s own meta already mounts
        // `WarningBadges` for this item (above, outside this expanded body),
        // so the editor's own copy would double the same pills. V1 mounts
        // (`AssignedRow`) omit this prop and keep both — pinned by
        // `EnrollQueue.send-integration.test.tsx`'s two-instance assertion.
        hideWarningBadges
        inputOptions={inputOptions}
        onSelectInputOption={onSelectInputOption}
        onRefreshInputOptionsIfStale={onRefreshInputOptionsIfStale}
        copyPrefill={copyPrefill}
        senderPrefill={senderPrefill}
        onSenderTouched={onSenderTouched}
        hasEditableCopy={hasEditableCopy}
        copyRequired={copyRequired}
        onAutosave={onAutosave}
        onFlushSave={onFlushSave}
        onSetStatus={handlers.handleSetStatus}
        onSend={handlers.handleSend}
        sending={sending}
        gongFlowId={gongFlowId}
        bodyFormat={bodyFormat}
        onEditStepOverride={onEditStepOverride}
        onResetStepOverride={onResetStepOverride}
        stepOverrideDraftFor={stepOverrideDraftFor}
        stepSaveErrorFor={stepSaveErrorFor}
        subscribeStepOverrides={subscribeStepOverrides}
        onSaveStepOverrides={onSaveStepOverrides}
        batchStepCopy={isBulkFollowEligible(item) ? batchStepCopy : undefined}
        /* U3 (D25) — substitute the Glaze `StepRows` for the editor's built-in
           `GongFlowStepsSection`. Every argument below comes from the editor's
           own already-resolved mount data, so nothing is re-derived here (D30):
           `locked` is its `sending || queued || readOnly || sendInProgress ||
           itemBusy` (E26/E27/D34) and `batchStepCopy` is the value passed
           above, already gated on `isBulkFollowEligible` (D24) — a frozen row
           therefore receives `undefined` and cannot start following a batch
           default mid-send.

           The persisted rung is `item.step_drafts`, looked up per step, which
           is exactly what the replaced mount passed as `overrides`. The draft
           callbacks are the context's, bound to THIS item — the same one-level
           binding `GongFlowStepsSection` does, so each row keeps subscribing
           to its own `${itemId}:${stepNumber}` store entry rather than
           re-rendering this subtree per keystroke. */
        stepsSection={({ itemId, gongFlowId: stepsFlowId, bodyFormat: stepsBodyFormat, locked, lockedTitle, batchStepCopy: rowBatchStepCopy }) => (
          <StepRows
            scope="lead"
            flowId={stepsFlowId}
            itemId={itemId}
            bodyFormat={stepsBodyFormat}
            disabled={locked}
            batchStepCopy={rowBatchStepCopy}
            overrideFor={stepNumber => item.step_drafts.find(draft => draft.step_number === stepNumber)}
            localDraftFor={stepNumber => stepOverrideDraftFor?.(itemId, stepNumber) ?? null}
            saveErrorFor={stepNumber => stepSaveErrorFor?.(itemId, stepNumber) ?? null}
            subscribeStep={(stepNumber, onChange) => subscribeStepOverrides?.(itemId, stepNumber, onChange) ?? NOOP_UNSUBSCRIBE}
            onEditStepOverride={(stepNumber, stepType, values) => onEditStepOverride?.(itemId, stepNumber, stepType, values)}
            onResetStepOverride={(stepNumber, stepType) => onResetStepOverride?.(itemId, stepNumber, stepType)}
            coolOff={{
              checked: item.cool_off_override,
              disabled: locked,
              disabledTitle: lockedTitle,
              onToggle: next => { void handlers.handleToggleCoolOff(item.id, next); },
              actingRepEmail: actingRepEmail ?? null,
            }}
          />
        )}
        onToggleCoolOff={handlers.handleToggleCoolOff}
        itemBusy={itemBusy}
        onBeginSendPipeline={onBeginSendPipeline}
        onEndSendPipeline={onEndSendPipeline}
        hideSendButton
      />
      {overriddenAccessory(item, readOnly, itemBusy, handlers.handleResetToBase, { senderTouched, baseSender })}
    </div>
  );
}
